import { useCallback, useEffect, useRef } from "react";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";
const SCRIPT_ID = "recaptcha-v3";
export const RECAPTCHA_ACTION = "submit_application";

function loadScript() {
  if (document.getElementById(SCRIPT_ID)) return;

  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
  script.async = true;
  document.head.appendChild(script);
}

/**
 * Loads Google reCAPTCHA v3 and returns an executor for a fresh token.
 *
 * The token is only ever a hint — the server verifies it (see backend/recaptcha.py).
 * When no site key is configured the hook is inert and `getToken` resolves to null,
 * so the form keeps working before keys are issued.
 *
 * @returns {{ isEnabled: boolean, getToken: () => Promise<string | null> }}
 */
export function useRecaptcha() {
  const isEnabled = Boolean(SITE_KEY);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!isEnabled || loadedRef.current) return;
    loadedRef.current = true;
    loadScript();
  }, [isEnabled]);

  const getToken = useCallback(async () => {
    if (!isEnabled) return null;

    const { grecaptcha } = window;
    if (!grecaptcha || !grecaptcha.execute) return null;

    try {
      return await new Promise((resolve, reject) => {
        grecaptcha.ready(() => {
          grecaptcha
            .execute(SITE_KEY, { action: RECAPTCHA_ACTION })
            .then(resolve)
            .catch(reject);
        });
      });
    } catch (error) {
      // Surface as "no token" — the server decides whether that is acceptable
      // rather than the browser silently deciding it passed.
      return null;
    }
  }, [isEnabled]);

  return { isEnabled, getToken };
}
